import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase/config';
import {
    collection, query, where, orderBy, onSnapshot, addDoc, updateDoc,
    doc, getDoc, serverTimestamp, increment
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { MessageSquare, Send, Image, ArrowLeft, Check, CheckCheck, Home, Languages } from 'lucide-react';
import { getOrCreateChatRoom, sendSystemMessage } from '../utils/chatUtils';
import { createNotification } from '../services/NotificationSystem';
import PageHeader from './common/PageHeader';

// StrictMode 중복 실행 방지용 (컴포넌트 외부 = 리마운트해도 유지)
const _processingInquiries = new Set();

function ChatRoom({ user }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [chatRooms, setChatRooms] = useState([]);
    const [internalRoom, setInternalRoom] = useState(null);
    const [selectedRoomId, setSelectedRoomId] = useState(searchParams.get('room') || null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [mobileShowChat, setMobileShowChat] = useState(false);

    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const inputRef = useRef(null);

    const currentUid = user?.companyId || user?.uid;

    // 🌏 번역 — 내 표시 언어 (해외 계정=en), 번역 결과는 메시지 문서에 캐시되어 onSnapshot으로 반영
    // 일본 계정은 ja, 그 외 해외는 en (2026-08-20 대표 지시 — 미국·일본·호주·캐나다 대응)
    const myLang = user?.labKind === 'overseas' ? (user?.country === 'Japan' ? 'ja' : 'en') : 'ko';
    const T9N_LABELS = {
        ko: { fail: '번역에 실패했습니다.', busy: '번역 중…', btn: '번역' },
        en: { fail: 'Translation failed. Please try again.', busy: 'Translating…', btn: 'Translate' },
        ja: { fail: '翻訳に失敗しました。もう一度お試しください。', busy: '翻訳中…', btn: '翻訳' },
    };
    const t9n = T9N_LABELS[myLang] || T9N_LABELS.ko;
    const [translatingIds, setTranslatingIds] = useState(new Set());
    const translateMsg = async (messageId) => {
        if (!selectedRoomId) return;
        setTranslatingIds(prev => new Set(prev).add(messageId));
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'translateChatMessage');
            await fn({ roomId: selectedRoomId, messageId, targetLang: myLang });
        } catch (e) {
            console.error('번역 실패:', e);
            alert(t9n.fail);
        } finally {
            setTranslatingIds(prev => { const n = new Set(prev); n.delete(messageId); return n; });
        }
    };

    // ── 마켓 문의 처리 (ProductDetail에서 navigate state로 전달) ──
    useEffect(() => {
        if (!location.state || !currentUid) return;
        const { type, targetUserId, productName, caseNo } = location.state;
        // product_inquiry(마켓) + design_outsource(CAD 디자인 외주) 모두 방 생성/열기 지원 (2026-07-25)
        if (!['product_inquiry', 'design_outsource'].includes(type) || !targetUserId) return;
        if (targetUserId === currentUid) return;

        // StrictMode 중복 실행 방지 (모듈 레벨 Set)
        const key = `${currentUid}_${targetUserId}`;
        if (_processingInquiries.has(key)) return;
        _processingInquiries.add(key);

        (async () => {
            try {
                const { roomId, created } = await getOrCreateChatRoom(
                    currentUid, targetUserId, type === 'design_outsource' ? 'design_outsource' : 'marketplace_inquiry'
                );
                if (created) {
                    await sendSystemMessage(roomId,
                        type === 'design_outsource'
                            ? `[디자인 외주] ${caseNo || ''} 케이스 협의가 시작되었습니다.`
                            : `[마켓 문의] ${productName || '상품'}에 대한 문의가 시작되었습니다.`
                    );
                }
                setSelectedRoomId(roomId);
                setMobileShowChat(true);
                setSearchParams({ room: roomId });
                navigate('/chat?room=' + roomId, { replace: true });
            } catch (e) {
                console.error('마켓 문의 채팅방 생성 실패:', e);
            } finally {
                // 3초 후 잠금 해제 (같은 문의 재진입 허용)
                setTimeout(() => _processingInquiries.delete(key), 3000);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, currentUid]);

    // ── 채팅방 리스트 실시간 구독 ──
    useEffect(() => {
        if (!currentUid) return;
        const q = query(
            collection(db, 'chatRooms'),
            where('participants', 'array-contains', currentUid),
            orderBy('lastMessageTime', 'desc')
        );
        const unsub = onSnapshot(q, async (snap) => {
            const mapped = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            // 내부(자체) 채팅방 분리
            const internal = mapped.find(r => r.type === 'internal');
            setInternalRoom(internal || null);

            // 일반 채팅방: internal 제외 + 상대방 있는 방만
            const regularRooms = mapped.filter(r => {
                if (r.type === 'internal') return false;
                const others = (r.participants || []).filter(p => p !== currentUid);
                return others.length > 0;
            });

            // participantInfo가 없는 방은 사용자 정보를 동적으로 가져오기
            for (const room of regularRooms) {
                const otherId = (room.participants || []).find(p => p !== currentUid);
                if (!otherId) continue;
                const otherInfo = room.participantInfo?.[otherId];
                if (!otherInfo || (!otherInfo.companyName && !otherInfo.name)) {
                    try {
                        const userSnap = await getDoc(doc(db, 'users', otherId));
                        if (userSnap.exists()) {
                            const ud = userSnap.data();
                            const info = {
                                name: ud.name || ud.displayName || '알 수 없음',
                                companyName: ud.businessName || ud.companyName || '',
                                companyType: ud.businessType || ud.companyBusinessType || '',
                            };
                            room.participantInfo = { ...room.participantInfo, [otherId]: info };
                            // Firestore에도 participantInfo 보완 저장
                            updateDoc(doc(db, 'chatRooms', room.id), {
                                [`participantInfo.${otherId}`]: info
                            }).catch(() => {});
                        }
                    } catch (e) {
                        // 조회 실패 시 기본값
                        room.participantInfo = { ...room.participantInfo, [otherId]: { name: '알 수 없음', companyName: '' } };
                    }
                }
            }

            setChatRooms(regularRooms);
        }, (err) => console.error('채팅방 리스트 오류:', err));
        return () => unsub();
    }, [currentUid]);

    // ── 선택된 방의 메시지 실시간 구독 ──
    useEffect(() => {
        if (!selectedRoomId) { setMessages([]); return; }
        const q = query(
            collection(db, 'chatRooms', selectedRoomId, 'messages'),
            orderBy('createdAt', 'asc')
        );
        const unsub = onSnapshot(q, (snap) => {
            setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        }, (err) => console.error('메시지 오류:', err));
        return () => unsub();
    }, [selectedRoomId]);

    // ── 방 선택 시 읽음 처리 ──
    useEffect(() => {
        if (!selectedRoomId || !currentUid) return;
        const roomRef = doc(db, 'chatRooms', selectedRoomId);
        updateDoc(roomRef, { [`unreadCount.${currentUid}`]: 0 }).catch(() => {});
    }, [selectedRoomId, currentUid, messages]);

    // ── 메시지 readBy 업데이트 ──
    useEffect(() => {
        if (!selectedRoomId || !currentUid || messages.length === 0) return;
        messages.forEach(msg => {
            if (msg.senderId !== currentUid && msg.id && !(msg.readBy || []).includes(currentUid)) {
                const msgRef = doc(db, 'chatRooms', selectedRoomId, 'messages', msg.id);
                updateDoc(msgRef, { readBy: [...(msg.readBy || []), currentUid] }).catch(() => {});
            }
        });
    }, [selectedRoomId, currentUid, messages]);

    // ── 스크롤 맨 아래로 ──
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // ── 방 선택 ──
    const selectRoom = (roomId) => {
        setSelectedRoomId(roomId);
        setMobileShowChat(true);
        setSearchParams({ room: roomId });
    };

    // ── 상대방 정보 가져오기 ──
    const getOtherParticipant = (room) => {
        if (!room.participantInfo || !currentUid) return { name: '알 수 없음', companyName: '' };
        const otherId = room.participants?.find(p => p !== currentUid);
        return room.participantInfo[otherId] || { name: '알 수 없음', companyName: '' };
    };

    // ── 메시지 전송 ──
    const handleSend = async () => {
        if ((!newMessage.trim() && !imageFile) || !selectedRoomId || sending) return;
        setSending(true);
        try {
            let content = newMessage.trim();
            let msgType = 'text';

            if (imageFile) {
                const storageRef = ref(storage, `chat/${selectedRoomId}/${Date.now()}_${imageFile.name}`);
                await uploadBytes(storageRef, imageFile);
                content = await getDownloadURL(storageRef);
                msgType = 'image';
            }

            const senderName = user?.businessName || user?.companyName || user?.name || '알 수 없음';
            await addDoc(collection(db, 'chatRooms', selectedRoomId, 'messages'), {
                senderId: currentUid,
                senderName,
                type: msgType,
                content,
                createdAt: serverTimestamp(),
                readBy: [currentUid],
            });

            // chatRoom 업데이트
            const roomRef = doc(db, 'chatRooms', selectedRoomId);
            const theRoom = chatRooms.find(r => r.id === selectedRoomId)
                || (internalRoom?.id === selectedRoomId ? internalRoom : null);
            const otherUids = (theRoom?.participants || []).filter(p => p !== currentUid);

            const updateData = {
                lastMessage: msgType === 'image' ? '[이미지]' : content,
                lastMessageTime: serverTimestamp(),
                lastMessageSenderId: currentUid,
            };
            otherUids.forEach(uid => {
                updateData[`unreadCount.${uid}`] = increment(1);
            });
            await updateDoc(roomRef, updateData);

            // 상대에게 채팅 알림 — 연속 메시지 스팸 방지: 같은 방은 10분에 1회만
            try {
                const notifKey = `chatNotif_${selectedRoomId}`;
                const last = Number(sessionStorage.getItem(notifKey) || 0);
                if (Date.now() - last > 10 * 60 * 1000) {
                    sessionStorage.setItem(notifKey, String(Date.now()));
                    await Promise.all(otherUids.map(uid => createNotification({
                        recipientId: uid,
                        type: 'chat_message',
                        title: '새 채팅 메시지',
                        message: `${senderName}: ${msgType === 'image' ? '[이미지]' : content.slice(0, 60)}`,
                        link: `/chat?room=${selectedRoomId}`,
                        metadata: { roomId: selectedRoomId },
                    })));
                }
            } catch (e) { console.warn('채팅 알림 실패:', e); }

            setNewMessage('');
            setImageFile(null);
            setImagePreview(null);
        } catch (e) {
            console.error('메시지 전송 실패:', e);
            alert('메시지 전송에 실패했습니다.');
        } finally {
            setSending(false);
            inputRef.current?.focus();
        }
    };

    // ── 이미지 선택 ──
    const handleImageSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { alert('10MB 이하의 이미지만 첨부 가능합니다.'); return; }
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    // ── 시간 포맷 ──
    const formatTime = (ts) => {
        if (!ts) return '';
        const date = ts.toDate ? ts.toDate() : new Date(ts);
        const now = new Date();
        const diff = now - date;
        if (diff < 60000) return '방금';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
        if (diff < 86400000 && now.getDate() === date.getDate()) {
            return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
        }
        if (diff < 86400000 * 2) return '어제';
        return `${date.getMonth() + 1}/${date.getDate()}`;
    };

    const formatMsgTime = (ts) => {
        if (!ts) return '';
        const date = ts.toDate ? ts.toDate() : new Date(ts);
        return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    };

    // ── 선택된 방 ──
    const selectedRoom = chatRooms.find(r => r.id === selectedRoomId)
        || (internalRoom?.id === selectedRoomId ? internalRoom : null);
    const isInternalSelected = selectedRoom?.type === 'internal';
    const otherInfo = selectedRoom
        ? (isInternalSelected
            ? { name: selectedRoom.companyName || '우리 업체', companyName: selectedRoom.companyName || '우리 업체' }
            : getOtherParticipant(selectedRoom))
        : null;
    const totalUnread = chatRooms.reduce((sum, r) => sum + (r.unreadCount?.[currentUid] || 0), 0)
        + (internalRoom?.unreadCount?.[currentUid] || 0);

    // ── 키보드 전송 ──
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!currentUid) {
        return (
            <div style={s.empty}>
                <MessageSquare size={48} color="#94a3b8" />
                <p style={{ color: '#64748b', marginTop: 16 }}>로그인이 필요합니다.</p>
            </div>
        );
    }

    return (
        <div style={s.wrapper}>
            <PageHeader title="채팅" icon={MessageSquare} />
            <div style={s.container}>
                {/* ── 좌측: 채팅방 리스트 ── */}
                <div
                    data-chat-list={mobileShowChat ? 'hidden' : 'visible'}
                    style={s.listPanel}
                >
                    <div style={s.listHeader}>
                        <h3 style={s.listTitle}>
                            메시지 {totalUnread > 0 && <span style={s.badge}>{totalUnread}</span>}
                        </h3>
                    </div>
                    <div style={s.roomList}>
                        {/* 자체(내부) 채팅방 */}
                        {internalRoom && (() => {
                            const internalUnread = internalRoom.unreadCount?.[currentUid] || 0;
                            const isActive = internalRoom.id === selectedRoomId;
                            return (
                                <div
                                    style={{
                                        ...s.roomItem,
                                        background: isActive ? '#fef3c7' : '#fffbeb',
                                        borderBottom: '2px solid #f59e0b',
                                    }}
                                    onClick={() => selectRoom(internalRoom.id)}
                                >
                                    <div style={{
                                        ...s.roomAvatar,
                                        background: '#f59e0b',
                                        color: '#fff',
                                    }}>
                                        <Home size={18} />
                                    </div>
                                    <div style={s.roomInfo}>
                                        <div style={s.roomTopRow}>
                                            <span style={s.roomName}>
                                                {internalRoom.companyName || '우리 업체'}
                                            </span>
                                            <span style={s.roomTime}>{formatTime(internalRoom.lastMessageTime)}</span>
                                        </div>
                                        <div style={s.roomBottomRow}>
                                            <span style={s.roomLastMsg}>
                                                {internalRoom.lastMessage || '내부 채팅방'}
                                            </span>
                                            {internalUnread > 0 && <span style={s.unreadBadge}>{internalUnread}</span>}
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {chatRooms.length === 0 && !internalRoom ? (
                            <div style={s.emptyList}>
                                <MessageSquare size={32} color="#cbd5e1" />
                                <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 8 }}>채팅방이 없습니다</p>
                            </div>
                        ) : chatRooms.map(room => {
                            const other = getOtherParticipant(room);
                            const unread = room.unreadCount?.[currentUid] || 0;
                            const isActive = room.id === selectedRoomId;
                            return (
                                <div
                                    key={room.id}
                                    style={{
                                        ...s.roomItem,
                                        ...(isActive ? s.roomItemActive : {}),
                                    }}
                                    onClick={() => selectRoom(room.id)}
                                >
                                    <div style={s.roomAvatar}>
                                        {(other.companyName || other.name || '?')[0]}
                                    </div>
                                    <div style={s.roomInfo}>
                                        <div style={s.roomTopRow}>
                                            <span style={s.roomName}>
                                                {other.companyName || other.name}
                                            </span>
                                            <span style={s.roomTime}>{formatTime(room.lastMessageTime)}</span>
                                        </div>
                                        <div style={s.roomBottomRow}>
                                            <span style={s.roomLastMsg}>
                                                {room.lastMessage || '대화를 시작해보세요'}
                                            </span>
                                            {unread > 0 && <span style={s.unreadBadge}>{unread}</span>}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ── 우측: 대화 화면 ── */}
                <div
                    data-chat-view={mobileShowChat ? 'visible' : 'hidden'}
                    style={s.chatPanel}
                >
                    {selectedRoom ? (
                        <>
                            {/* 헤더 */}
                            <div style={s.chatHeader}>
                                <button
                                    data-chat-back
                                    style={s.backBtn}
                                    onClick={() => { setMobileShowChat(false); }}
                                >
                                    <ArrowLeft size={20} />
                                </button>
                                <div>
                                    <div style={s.chatHeaderName}>{otherInfo?.companyName || otherInfo?.name}</div>
                                    {otherInfo?.companyName && otherInfo?.name && otherInfo.companyName !== otherInfo.name && (
                                        <div style={s.chatHeaderSub}>{otherInfo.name}</div>
                                    )}
                                </div>
                            </div>

                            {/* 메시지 목록 */}
                            <div style={s.messageList}>
                                {messages.map((msg, idx) => {
                                    const isMine = msg.senderId === currentUid;
                                    const isSystem = msg.type === 'system';
                                    const showDate = idx === 0 || !isSameDay(
                                        messages[idx - 1]?.createdAt, msg.createdAt
                                    );
                                    const otherParticipants = selectedRoom.participants?.filter(p => p !== currentUid) || [];
                                    const isRead = otherParticipants.every(p => (msg.readBy || []).includes(p));

                                    return (
                                        <React.Fragment key={msg.id || idx}>
                                            {showDate && msg.createdAt && (
                                                <div style={s.dateDivider}>
                                                    <span style={s.dateText}>
                                                        {formatDateDivider(msg.createdAt)}
                                                    </span>
                                                </div>
                                            )}
                                            {isSystem ? (
                                                <div style={s.systemMsg}>{msg.content}</div>
                                            ) : (
                                                <div style={{
                                                    ...s.msgRow,
                                                    justifyContent: isMine ? 'flex-end' : 'flex-start',
                                                }}>
                                                    {!isMine && (
                                                        <div style={s.msgAvatar}>
                                                            {(msg.senderName || '?')[0]}
                                                        </div>
                                                    )}
                                                    <div style={{
                                                        ...s.msgContent,
                                                        alignItems: isMine ? 'flex-end' : 'flex-start',
                                                    }}>
                                                        {!isMine && (
                                                            <span style={s.msgSenderName}>{msg.senderName}</span>
                                                        )}
                                                        {msg.type === 'image' ? (
                                                            <img
                                                                src={msg.content}
                                                                alt="첨부 이미지"
                                                                style={s.msgImage}
                                                                onClick={() => window.open(msg.content, '_blank')}
                                                            />
                                                        ) : (
                                                            <div style={{
                                                                ...s.msgBubble,
                                                                ...(isMine ? s.myBubble : s.otherBubble),
                                                            }}>
                                                                {msg.content}
                                                                {msg.translations?.[myLang] && (
                                                                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(100,116,139,.25)', fontSize: 13, opacity: .85 }}>
                                                                        <Languages size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{msg.translations[myLang]}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {msg.type !== 'image' && !isMine && !msg.translations?.[myLang] && (
                                                            <button
                                                                onClick={() => translateMsg(msg.id)}
                                                                disabled={translatingIds.has(msg.id)}
                                                                style={{ marginTop: 3, border: 'none', background: 'none', cursor: 'pointer', color: '#818cf8', fontSize: 11.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3, padding: 0 }}>
                                                                <Languages size={12} />{translatingIds.has(msg.id) ? t9n.busy : t9n.btn}
                                                            </button>
                                                        )}
                                                        <div style={s.msgMeta}>
                                                            {isMine && (
                                                                <span style={{ marginRight: 4, display: 'flex', alignItems: 'center' }}>
                                                                    {isRead
                                                                        ? <CheckCheck size={14} color="#6366f1" />
                                                                        : <Check size={14} color="#94a3b8" />
                                                                    }
                                                                </span>
                                                            )}
                                                            <span style={s.msgTime}>{formatMsgTime(msg.createdAt)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* 이미지 프리뷰 */}
                            {imagePreview && (
                                <div style={s.imagePreviewBar}>
                                    <img src={imagePreview} alt="미리보기" style={s.imagePreviewThumb} />
                                    <span style={{ flex: 1, fontSize: 13, color: '#475569' }}>{imageFile?.name}</span>
                                    <button style={s.removePreview} onClick={() => { setImageFile(null); setImagePreview(null); }}>
                                        &times;
                                    </button>
                                </div>
                            )}

                            {/* 입력 영역 */}
                            <div style={s.inputArea}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleImageSelect}
                                />
                                <button style={s.attachBtn} onClick={() => fileInputRef.current?.click()}>
                                    <Image size={20} color="#64748b" />
                                </button>
                                <input
                                    ref={inputRef}
                                    style={s.input}
                                    placeholder="메시지를 입력하세요..."
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    disabled={sending}
                                />
                                <button
                                    style={{
                                        ...s.sendBtn,
                                        opacity: (!newMessage.trim() && !imageFile) || sending ? 0.4 : 1,
                                    }}
                                    onClick={handleSend}
                                    disabled={(!newMessage.trim() && !imageFile) || sending}
                                >
                                    <Send size={20} color="#fff" />
                                </button>
                            </div>
                        </>
                    ) : (
                        <div style={s.noChatSelected}>
                            <MessageSquare size={48} color="#cbd5e1" />
                            <p style={{ color: '#94a3b8', marginTop: 16 }}>채팅방을 선택해주세요</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── 유틸 ──
function isSameDay(ts1, ts2) {
    if (!ts1 || !ts2) return false;
    const d1 = ts1.toDate ? ts1.toDate() : new Date(ts1);
    const d2 = ts2.toDate ? ts2.toDate() : new Date(ts2);
    return d1.getFullYear() === d2.getFullYear()
        && d1.getMonth() === d2.getMonth()
        && d1.getDate() === d2.getDate();
}

function formatDateDivider(ts) {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 ${days[d.getDay()]}요일`;
}

// ── 스타일 ──
const s = {
    wrapper: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    container: {
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
        background: '#fff',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
    },

    // ── 리스트 패널 ──
    listPanel: {
        width: 320,
        minWidth: 320,
        borderRight: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        background: '#fafbfc',
    },
    listHeader: {
        padding: '16px 20px',
        borderBottom: '1px solid #e2e8f0',
    },
    listTitle: {
        margin: 0,
        fontSize: 16,
        fontWeight: 700,
        color: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    badge: {
        background: '#6366f1',
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        borderRadius: 10,
        padding: '2px 7px',
        minWidth: 18,
        textAlign: 'center',
    },
    roomList: {
        flex: 1,
        overflowY: 'auto',
    },
    emptyList: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
    },
    roomItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 20px',
        cursor: 'pointer',
        transition: 'background 0.15s',
        borderBottom: '1px solid #f1f5f9',
    },
    roomItemActive: {
        background: '#eef2ff',
    },
    roomAvatar: {
        width: 44,
        height: 44,
        borderRadius: '50%',
        background: '#e0e7ff',
        color: '#4f46e5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 16,
        fontWeight: 700,
        flexShrink: 0,
    },
    roomInfo: {
        flex: 1,
        minWidth: 0,
    },
    roomTopRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 4,
    },
    roomName: {
        fontSize: 14,
        fontWeight: 600,
        color: '#0f172a',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    roomTime: {
        fontSize: 11,
        color: '#94a3b8',
        flexShrink: 0,
        marginLeft: 8,
    },
    roomBottomRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    roomLastMsg: {
        fontSize: 13,
        color: '#64748b',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1,
    },
    unreadBadge: {
        background: '#ef4444',
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        borderRadius: 10,
        padding: '2px 7px',
        minWidth: 18,
        textAlign: 'center',
        marginLeft: 8,
        flexShrink: 0,
    },

    // ── 대화 패널 ──
    chatPanel: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    chatHeader: {
        padding: '12px 20px',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: '#fff',
    },
    backBtn: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 4,
        display: 'none', // 모바일에서만 보임
        color: '#475569',
    },
    chatHeaderName: {
        fontSize: 15,
        fontWeight: 700,
        color: '#0f172a',
    },
    chatHeaderSub: {
        fontSize: 12,
        color: '#64748b',
    },
    noChatSelected: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    empty: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400,
    },

    // ── 메시지 ──
    messageList: {
        flex: 1,
        overflowY: 'auto',
        padding: '16px 20px',
        background: '#f8fafc',
    },
    dateDivider: {
        textAlign: 'center',
        margin: '16px 0',
    },
    dateText: {
        fontSize: 12,
        color: '#94a3b8',
        background: '#e2e8f0',
        padding: '4px 12px',
        borderRadius: 12,
    },
    systemMsg: {
        textAlign: 'center',
        fontSize: 12,
        color: '#6366f1',
        background: '#eef2ff',
        padding: '6px 14px',
        borderRadius: 12,
        margin: '8px auto',
        maxWidth: '80%',
        width: 'fit-content',
    },
    msgRow: {
        display: 'flex',
        gap: 8,
        marginBottom: 8,
        alignItems: 'flex-end',
    },
    msgAvatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: '#e0e7ff',
        color: '#4f46e5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 700,
        flexShrink: 0,
        alignSelf: 'flex-start',
        marginTop: 2,
    },
    msgContent: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '70%',
    },
    msgSenderName: {
        fontSize: 11,
        color: '#64748b',
        marginBottom: 2,
        marginLeft: 4,
    },
    msgBubble: {
        padding: '10px 14px',
        borderRadius: 16,
        fontSize: 14,
        lineHeight: 1.5,
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
    },
    myBubble: {
        background: '#6366f1',
        color: '#fff',
        borderBottomRightRadius: 4,
    },
    otherBubble: {
        background: '#fff',
        color: '#0f172a',
        border: '1px solid #e2e8f0',
        borderBottomLeftRadius: 4,
    },
    msgImage: {
        maxWidth: 240,
        maxHeight: 240,
        borderRadius: 12,
        cursor: 'pointer',
        objectFit: 'cover',
    },
    msgMeta: {
        display: 'flex',
        alignItems: 'center',
        marginTop: 2,
        paddingLeft: 4,
        paddingRight: 4,
    },
    msgTime: {
        fontSize: 10,
        color: '#94a3b8',
    },

    // ── 이미지 프리뷰 ──
    imagePreviewBar: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 16px',
        background: '#f1f5f9',
        borderTop: '1px solid #e2e8f0',
    },
    imagePreviewThumb: {
        width: 40,
        height: 40,
        borderRadius: 6,
        objectFit: 'cover',
    },
    removePreview: {
        background: 'none',
        border: 'none',
        fontSize: 20,
        cursor: 'pointer',
        color: '#64748b',
        padding: '0 4px',
    },

    // ── 입력 영역 ──
    inputArea: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 16px',
        borderTop: '1px solid #e2e8f0',
        background: '#fff',
    },
    attachBtn: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 6,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
    },
    input: {
        flex: 1,
        border: '1px solid #e2e8f0',
        borderRadius: 20,
        padding: '10px 16px',
        fontSize: 14,
        outline: 'none',
        transition: 'border-color 0.2s',
    },
    sendBtn: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: '#6366f1',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
};

// ── 반응형 CSS 주입 ──
(function injectChatStyles() {
    if (document.getElementById('chatroom-responsive-css')) return;
    const style = document.createElement('style');
    style.id = 'chatroom-responsive-css';
    style.textContent = `
@media (max-width: 768px) {
    [data-chat-list] { width: 100% !important; min-width: 100% !important; border-right: none !important; }
    [data-chat-list="hidden"] { display: none !important; }
    [data-chat-view="hidden"] { display: none !important; }
    [data-chat-view] { width: 100% !important; }
    [data-chat-back] { display: flex !important; }
}
`;
    document.head.appendChild(style);
})();

export default ChatRoom;
